import { Alert, Button, Stack, TextField, Typography } from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { GqlProjectUserInviteInput } from "~/__generated__";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";

import useProjectUserInvite from "../../api/useProjectUserCreate";
import useRequireCurrentProject from "../../hooks/useRequireCurrentProject";

type Props = {
    onSubmitted: () => void;
};

export default function CreateProjectUserForm({ onSubmitted }: Props) {
    const { t } = useTranslation("user");
    const { project } = useRequireCurrentProject();
    const { inviteProjectUser, error } = useProjectUserInvite();
    const {
        handleSubmit,
        register,
        setError,
        formState: { errors, isSubmitting, isDirty, isSubmitted, isLoading },
    } = useForm<Omit<GqlProjectUserInviteInput, "projectId">>({
        values: { email: "" },
    });

    const onSubmit: SubmitHandler<Omit<GqlProjectUserInviteInput, "projectId">> = async ({ email }) => {
        if (!project) {
            return;
        }
        await inviteProjectUser(project.id, { email });
        onSubmitted();
    };

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {errors.root && (
                    <Alert severity="error">
                        <Typography>{errors.root.message}</Typography>
                    </Alert>
                )}

                <TextField
                    label={t("userCreateForm.labelEmail")}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    {...register("email", {
                        required: t("userCreateForm.emailRequired"),
                        pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: t("userCreateForm.emailInvalid"),
                        },
                    })}
                />

                <Button
                    sx={{ mt: 3 }}
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={!isDirty}
                    loading={isSubmitting || isLoading}
                >
                    {t("userCreateForm.sendInvite")}
                </Button>
            </Stack>
        </form>
    );
}
